import { type ChangeEvent, type KeyboardEvent, useEffect, useState } from 'react';
import {
  AdornedInput,
  Box,
  Checkbox,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Text,
} from '@zillow/constellation-10';
import { isValidNumberKeyDown } from '../../utils/isValidNumberKeyDown';

type HousingVoucherFieldProps = {
  hasHousingVoucher: boolean;
  voucherAmountMonthly: number | null;
  monthlyIncome: number | null;
  onHousingVoucherChange: (hasVoucher: boolean) => void;
  onVoucherAmountChange: (amount: number | null) => void;
};

const formatCurrency = (num: number): string => num.toLocaleString('en-US');

const parseCurrency = (str: string): number | null => {
  const cleaned = str.replace(/,/g, '');
  const num = Number(cleaned);
  return isNaN(num) ? null : num;
};

export const HousingVoucherField = ({
  hasHousingVoucher,
  voucherAmountMonthly,
  monthlyIncome,
  onHousingVoucherChange,
  onVoucherAmountChange,
}: HousingVoucherFieldProps) => {
  const [localAmount, setLocalAmount] = useState<number | null>(null);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [showError, setShowError] = useState(false);

  // Initialize display value from prop
  useEffect(() => {
    if (voucherAmountMonthly !== null && voucherAmountMonthly !== undefined) {
      setLocalAmount(voucherAmountMonthly);
      setDisplayValue(formatCurrency(voucherAmountMonthly));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    onHousingVoucherChange(checked);

    if (!checked) {
      // Clear voucher amount when unchecking
      setLocalAmount(null);
      setDisplayValue('');
      setShowError(false);
      onVoucherAmountChange(null);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const parsed = parseCurrency(inputValue);

    if (parsed === null || parsed === 0) {
      setDisplayValue('');
      setLocalAmount(null);
      return;
    }

    setDisplayValue(formatCurrency(parsed));
    setLocalAmount(parsed);
  };

  const handleBlur = () => {
    if (hasHousingVoucher && localAmount === null) {
      setShowError(true);
    } else {
      setShowError(false);
    }

    onVoucherAmountChange(localAmount);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isValidNumberKeyDown(e.key)) {
      e.preventDefault();
    }
  };

  const totalIncome =
    hasHousingVoucher && localAmount !== null && monthlyIncome !== null && monthlyIncome > 0
      ? localAmount + monthlyIncome
      : null;

  return (
    <Box css={{ marginBlockEnd: 'looser' }}>
      <LabeledControl
        control={
          <Checkbox
            data-testid="housing-voucher-checkbox"
            checked={hasHousingVoucher}
            onChange={handleCheckboxChange}
          />
        }
        label={<Label data-testid="housing-voucher-label">I have a housing voucher</Label>}
      />

      {hasHousingVoucher && (
        <Box css={{ marginTop: 'default' }}>
          <FormField
            label={<Label data-testid="voucher-amount-label">Voucher amount</Label>}
            control={
              <AdornedInput
                input={
                  <AdornedInput.Input
                    data-testid="voucher-amount-input"
                    type="text"
                    inputMode="numeric"
                    value={displayValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                  />
                }
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
                fluid
              />
            }
            description={
              showError ? (
                <FormHelp data-testid="voucher-amount-error" error>
                  Enter your voucher amount
                </FormHelp>
              ) : (
                <FormHelp data-testid="voucher-amount-help-text">
                  This helps the landlord understand your total income
                </FormHelp>
              )
            }
            error={showError}
          />

          {totalIncome !== null && (
            <Text
              data-testid="housing-voucher-total-income"
              textStyle="body-sm"
              css={{ marginTop: 'tighter', color: 'text.subtle' }}
            >
              Your total income and voucher amount is ${formatCurrency(totalIncome)}
              /month.
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
